<template>
	<div class="suggested-posts-container">
		<section class="suggested-posts-calendar">
			<div class="suggested-posts-intro">
				<h1>Suggested Posts</h1>
				<p class="suggested-posts-description">
					Save time and organize your social media calendar by using our
					prepared posts.
				</p>
			</div>
			<MiniCalendar
				@clicked="receiveDate"
				@toggleCalendar="toggleCalendar"
				:postDatesThisMonth="postDatesThisMonth"
				:postDatesNextMonth="postDatesNextMonth"
			/>
			<button
				class="b360-btn--workspace-primary custom-post-button"
				data-test="custom-post-button"
				@click="handleCreatePostClick()"
			>
				Create Custom Post
			</button>
		</section>
		<section class="suggested-posts-list">
			<div class="suggested-posts-intro">
				<h1>Suggested Posts</h1>
			</div>
			<ul
				id="suggested-post-date-list-current"
				class="suggested-posts-date-list"
			>
				<li
					v-for="(date, index) in currentMonthPosts"
					:key="index"
					class="suggested-posts-date-item date-item-this-month"
				>
					<DateSquare
						:date="date"
						:month="currentMonth"
						:class="{ 'current-date': date === currentDate }"
					/>
					<div
						class="date-item-post-list"
						v-for="(posts, postsIndex) in showDaysPosts(thisMonthPosts, date)"
						:key="postsIndex"
					>
						<SuggestedPost
							v-for="(post, postIndex) in posts.items"
							:id="date"
							:key="postIndex"
							:image="post.previewUri"
							:title="post.title"
							:content="post.description"
							@click.native="handleSuggestedPostClick(post, posts.date)"
						/>
					</div>
					<SuggestedPost v-if="!findDate(postDatesThisMonth, date)" />
				</li>
			</ul>
			<ul id="suggested-post-date-list-next" class="suggested-posts-date-list">
				<li
					v-for="(nextMonthDate, index) in daysNextMonth"
					:key="index"
					class="suggested-posts-date-item date-item-next-month"
				>
					<DateSquare :date="nextMonthDate" :month="nextMonth" />
					<div
						class="date-item-post-list"
						v-for="(nextMonthPosts, postsIndex) in showDaysPosts(
							nextMonthPosts,
							nextMonthDate,
						)"
						:key="postsIndex"
					>
						<SuggestedPost
							v-for="(nextMonthPost, postIndex) in nextMonthPosts.items"
							:key="postIndex"
							:image="nextMonthPost.previewUri"
							:title="nextMonthPost.title"
							:content="nextMonthPost.description"
							@click.native="
								handleSuggestedPostClick(nextMonthPost, nextMonthPosts.date)
							"
						/>
					</div>
					<SuggestedPost
						v-if="!findDate(postDatesNextMonth, nextMonthDate)"
						@createPostClick="handleCreatePostClick()"
					/>
				</li>
			</ul>
		</section>

		<SocialComposer
			:image="image"
			:content="content"
			:isCreatePost="isCreatePost"
			:mediaData="mediaData"
			:mediaTypeId="mediaTypeId"
			:campaignId="campaignId"
			ref="socialComposer"
			@socialComposerClose="handleSocialComposerClose"
		/>
	</div>
</template>

<script>
import moment from 'moment';
import MiniCalendar from '@/components/marketing-calendar/MiniCalendar.vue';
import DateSquare from '@/components/marketing-calendar/DateSquare.vue';
import SuggestedPost from '@/components/marketing-calendar/SuggestedPost.vue';

import SuggestedPostService from '@/services/toolbox/suggested-post-service';
import SocialComposer from '@/components/global/SocialComposer.vue';

export default {
	name: 'MarketingCalendarSuggested',
	components: {
		MiniCalendar,
		DateSquare,
		SuggestedPost,
		SocialComposer,
	},
	data() {
		return {
			today: moment(),
			timeModalClosed: new Date(),
			scheduleDate: new Date(),
			mediaData: [],
			isCreatePost: false,
			suggestedPosts: [],
			thisMonthPosts: [],
			nextMonthPosts: [],
			months: [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sep',
				'Oct',
				'Nov',
				'Dec',
			],
			postDatesThisMonth: [],
			postDatesNextMonth: [],
			calendarIsOpen: false,
			image: null,
			content: null,
			campaignId: null,
			mediaTypeId: null,
		};
	},

	computed: {
		currentMonthPosts() {
			return this.today.daysInMonth();
		},
		currentDate() {
			return this.today.get('date');
		},
		currentMonth() {
			return this.today.format('MMM');
		},
		year() {
			return this.today.format('YYYY');
		},
		monthFullName() {
			return this.today.format('MMMM');
		},
		nextMonth() {
			return moment(this.today).add(1, 'month').format('MMM');
		},
		nextMonthFullName() {
			return moment(this.today).add(1, 'month').format('MMMM');
		},
		nextYear() {
			return moment(this.today).add(1, 'month').format('YYYY');
		},
		daysNextMonth() {
			return moment(this.today).add(1, 'month').daysInMonth();
		},
	},
	methods: {
		receiveDate(value, month) {
			let dateList = '';

			if (month === this.monthFullName) {
				dateList = document.getElementsByClassName('date-item-this-month');
			} else {
				dateList = document.getElementsByClassName('date-item-next-month');
			}

			[].forEach.call(dateList, (dateItem) => {
				const dateSquare =
					dateItem.getElementsByClassName('date-square-date')[0];

				if (dateSquare.innerText === value.toString()) {
					if (window.innerWidth < 1024 && this.calendarIsOpen) {
						window.scroll(0, dateSquare.offsetTop - 560);
					} else if (window.innerWidth < 1024 && !this.calendarIsOpen) {
						window.scroll(0, dateSquare.offsetTop - 265);
					} else {
						window.scroll(0, dateSquare.offsetTop - 308);
					}
				}
			});
		},
		sendData({ previewUri, description, medias, campaignId }) {
			this.image = previewUri;
			this.content = description;
			this.mediaData = [medias[0].uri];
			this.mediaTypeId = medias[0].mediaTypeId || 1;
			this.campaignId = campaignId;
		},
		clearData() {
			this.image = null;
			this.content = null;
			this.mediaData = [];
			this.mediaTypeId = null;
			this.campaignId = null;
			this.isCreatePost = false;
		},
		handleSuggestedPostClick(post, postsDate) {
			const msBeforeCanOpenModal = 1500;
			// Calculate the milliseconds since the modal was last closed
			const msSinceClosedModal = new Date().getTime() - this.timeModalClosed.getTime();
			// Determine the remaining time before the modal can be opened again
			const remainingTime = msBeforeCanOpenModal - msSinceClosedModal;
	
			// Define a function to execute the modal-related operations
			const openModal = () => {
					this.sendData(post);
					this.$refs.socialComposer.validateShowModal();
					this.$refs.socialComposer.handleModalDateClick(postsDate);
			};
	
			/*
			* If the remaining time is less than or equal to 0, execute the operations immediately.
			* Otherwise, delay the execution until the remaining time has elapsed.
			* This ensures the modal operations are not initiated too quickly in succession,
			* allowing for any necessary preparations, such as loading external resources.
			*/
			if (remainingTime <= 0) {
					openModal();
			} else {
					setTimeout(openModal, remainingTime);
			}
		},
		handleCreatePostClick() {
			this.isCreatePost = true;
			this.$refs.socialComposer.validateShowModal();
			this.$refs.socialComposer.handleCreatePost();
		},
		handleSocialComposerClose() {
			this.timeModalClosed = new Date();
			this.clearData();
		},

		findDate(month, date) {
			return month.includes(date);
		},
		toggleCalendar(isOpen) {
			this.calendarIsOpen = isOpen;
		},
		showDaysPosts(posts, date) {
			//iterate posts filter
			return posts.filter((post) => post.date.getDate() === date);
		},
	},
	created() {
		SuggestedPostService.getSuggestedPosts()
			.then((response) => {
				this.suggestedPosts = Object.keys(response.data.posts).map((x) => ({
					date: new Date(x),
					items: response.data.posts[x],
				}));
				this.thisMonthPosts = this.suggestedPosts.filter(
					(x) => x.date < moment(this.today).endOf('month'),
				);
				this.nextMonthPosts = this.suggestedPosts.filter(
					(x) =>
						x.date > moment(this.today).endOf('month') &&
						x.date < moment(this.today).add(1, 'month').endOf('month'),
				);
				this.postDatesThisMonth = this.thisMonthPosts.map((x) =>
					x.date.getDate(),
				);
				this.postDatesNextMonth = this.nextMonthPosts.map((x) =>
					x.date.getDate(),
				);
			})
			.finally(() => {
				this.receiveDate(this.currentDate, this.monthFullName);
			});
	},
	updated() {
		if (this.showModal) {
			document.getElementsByTagName('body')[0].style.overflow = 'hidden';
		} else {
			document.getElementsByTagName('body')[0].style.overflow = 'auto';
		}
	},
};
</script>

<style lang="scss" scoped>
.suggested-posts-container {
	@include rem(padding, 0 30px);
	display: flex;

	h1 {
		margin: 0;
	}
}

.suggested-posts {
	&-calendar {
		align-self: flex-start;
		position: sticky;
		@include rem(top, 170px);
		display: inline-block;
		@include rem(margin-right, 30px);

		@include phablet {
			display: block;
		}

		.mini-calendar-container {
			@include rem(min-width, 320px);
		}
	}

	&-description {
		@include rem(margin, 8px 0 20px);
		@include rem(padding-bottom, 20px);
		font-size: $b360-font-size--small;
		@include rem(line-height, 25px);
	}

	&-list {
		display: inline-block;
		vertical-align: top;
		@include rem(padding-top, 30px);

		.suggested-posts-intro {
			display: none;
		}
	}

	&-date-list {
		list-style-type: none;
		padding: 0;
		margin-top: 0;
	}

	&-date-item {
		@include rem(margin-bottom, 30px);
		display: grid;
		grid-template-columns: 70px 1fr;
		@include rem(grid-gap, 15px);
	}
}

.date-item-post-list {
	display: grid;
	grid-template-columns: 1fr;
	@include rem(grid-gap, 15px);
}

.custom-post-button {
	margin-top: 1rem;
}
.suggested-post {
	cursor: pointer;
}

@media (max-width: $mobile-max) {
	.suggested-posts {
		&-container {
			@include rem(padding, 0 15px);
		}

		&-calendar {
			position: sticky;

			.mini-calendar-container {
				width: 100%;
				min-width: unset;
			}
		}

		&-date-list {
			.date-square {
				@include rem(width, 60px);
				@include rem(height, 73px);
			}
		}

		&-date-item {
			grid-template-columns: 60px 1fr;
		}
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity $b360-motion-duration--shortest
		$b360-motion-easing--standard;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}

@media (max-width: 651px) {
	.suggested-post {
		grid-template-columns: 1fr;
	}
}

@media (min-width: calc($breakpoint + 1px)) and (max-width: 1291px) {
	.suggested-post {
		grid-template-columns: 1fr;
	}
}

@media (max-width: $breakpoint) {
	.suggested-posts {
		&-container {
			flex-direction: column;
		}

		&-calendar {
			top: calc(#{$top-nav-height + $context-nav-height});
			background-color: $off-white;
			display: block;
			width: 100%;
			margin-right: 0;
			@include rem(padding-top, 20px);

			.suggested-posts-intro {
				display: none;
			}
		}

		&-list {
			@include rem(padding-top, 20px);

			.suggested-posts-intro {
				display: block;
				@include rem(margin-bottom, 20px);
			}
		}
	}

	.mini-calendar-container {
		margin: 0 auto;
	}
}
</style>
